// src/components/StatisticsDashboard.js
import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  Area,
  AreaChart,
} from "recharts";
import {
  ChartBarIcon,
  UsersIcon,
  EnvelopeIcon,
  ArrowTrendingUpIcon,
} from "@heroicons/react/24/solid";
import { SERVER_BASE_URL } from "../config";

/* -------------------------- helpers / ui bits -------------------------- */

// единая палитра
const PALETTE = {
  confirmed: "#6366F1", // indigo-500
  planned: "#10B981", // emerald-500
  cancelled: "#F59E0B", // amber-500
  invited: "#A78BFA",
  attended: "#34D399",
  series1: "#8884d8",
  series2: "#82ca9d",
  series3: "#FFBB28",
  gray: "#E5E7EB",
};

const COLORS = [
  "#8884d8",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#82ca9d",
  "#A78BFA",
];

// форматтеры
const fmt = (n) =>
  typeof n === "number" ? n.toLocaleString(undefined) : n ?? "";
const pct = (n) => `${Number(n ?? 0).toFixed(0)}%`;
const fmtDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// бейдж дельты
function DeltaBadge({ value }) {
  if (value == null || value === 0) return null;
  const up = value > 0;
  return (
    <span
      className={`ml-2 inline-flex items-center text-xs px-1.5 py-0.5 rounded ${
        up ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
      }`}
    >
      {up ? "▲" : "▼"} {Math.abs(value)}%
    </span>
  );
}
DeltaBadge.propTypes = { value: PropTypes.number };

// skeleton вместо спиннера
const Skeleton = () => (
  <div className="animate-pulse">
    <div className="h-5 bg-gray-200 rounded w-1/3 mb-3" />
    <div className="h-48 bg-gray-100 rounded" />
  </div>
);

// подпись в pie только если >5%
const renderCustomPieLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
}) => {
  if (percent < 0.05) return null;
  const RADIAN = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize="12"
      fontWeight="bold"
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

// экспорт CSV
const downloadCSV = (rows, filename) => {
  if (!rows || rows.length === 0) return;
  const header = Object.keys(rows[0]).join(",");
  const body = rows
    .map((r) =>
      Object.values(r)
        .map((v) =>
          String(v ?? "")
            .replaceAll('"', '""')
            .includes(",")
            ? `"${String(v).replaceAll('"', '""')}"`
            : String(v ?? "")
        )
        .join(",")
    )
    .join("\n");
  const blob = new Blob([header + "\n" + body], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

/* --------------------------------- main -------------------------------- */

const StatisticsDashboard = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [timeRange, setTimeRange] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const loadStatistics = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem("token");
        const resp = await fetch(
          `${SERVER_BASE_URL}/api/statistics/overview?time_range=${timeRange}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!resp.ok) {
          const text = await resp.text();
          console.error("Overview error:", resp.status, text);
          throw new Error(`HTTP ${resp.status}`);
        }
        const data = await resp.json();
        if (!cancelled) setStats(data);
      } catch (e) {
        console.error("Error loading statistics:", e);
        if (!cancelled) setStats(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    loadStatistics();
    return () => {
      cancelled = true;
    };
  }, [timeRange]);

  // Guests detail when tab == 'guests'
  useEffect(() => {
    let cancelled = false;
    const loadGuestsStatistics = async () => {
      if (activeTab !== "guests") return;
      try {
        setIsLoading(true);
        const token = localStorage.getItem("token");
        const resp = await fetch(
          `${SERVER_BASE_URL}/api/statistics/guests?time_range=${timeRange}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!resp.ok) {
          const text = await resp.text();
          console.error("Guests error:", resp.status, text);
          throw new Error(`HTTP ${resp.status}`);
        }
        const data = await resp.json();
        if (!cancelled)
          setStats((prev) => ({
            ...prev,
            ...data,
          }));
      } catch (e) {
        console.error("Error loading guest statistics:", e);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    loadGuestsStatistics();
    return () => {
      cancelled = true;
    };
  }, [activeTab, timeRange]);

  // derived metrics
  const derived = useMemo(() => {
    const invited = stats?.guestStats?.totalInvited ?? 0;
    const attended = stats?.guestStats?.totalAttended ?? 0;
    const overallRate = invited ? Math.round((attended / invited) * 100) : 0;

    // пример использования capacity (если backend это отдаёт)
    const withCap = (stats?.eventsWithCapacity || []).filter(
      (e) => e.capacity > 0
    );
    const avgUtil = withCap.length
      ? Math.round(
          withCap.reduce((s, e) => s + (e.attended / e.capacity) * 100, 0) /
            withCap.length
        )
      : null;

    return { overallRate, avgUtil };
  }, [stats]);

  // таблицы: сортировка Guest Sources
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [guestNameQuery, setGuestNameQuery] = useState("");

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span className="ml-1 text-gray-400">–</span>;
    return sortDirection === "asc" ? (
      <span className="ml-1 text-gray-600" style={{ fontSize: "11px" }}>
        ▲
      </span>
    ) : (
      <span className="ml-1 text-gray-600" style={{ fontSize: "11px" }}>
        ▼
      </span>
    );
  };
  SortIcon.propTypes = { field: PropTypes.string.isRequired };

  const guestNameRows = useMemo(() => {
    const rows = (stats?.guestNameStats || []).filter((guest) =>
      (guest.name || "").toLowerCase().includes(guestNameQuery.trim().toLowerCase())
    );
    const guestFields = new Set([
      "guestName",
      "addedCount",
      "attendedCount",
      "attendanceRate",
      "eventCount",
      "lastEventAt",
    ]);

    const sortedRows = [...rows];
    if (!guestFields.has(sortField)) {
      return sortedRows.sort(
        (a, b) =>
          (b.attendedCount || 0) - (a.attendedCount || 0) ||
          (b.addedCount || 0) - (a.addedCount || 0) ||
          (a.name || "").localeCompare(b.name || "")
      );
    }

    sortedRows.sort((a, b) => {
      let aValue;
      let bValue;

      if (sortField === "guestName") {
        aValue = a.name || "";
        bValue = b.name || "";
        return sortDirection === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (sortField === "lastEventAt") {
        aValue = a.lastEventAt ? new Date(a.lastEventAt).getTime() : 0;
        bValue = b.lastEventAt ? new Date(b.lastEventAt).getTime() : 0;
      } else {
        aValue = Number(a[sortField] || 0);
        bValue = Number(b[sortField] || 0);
      }

      return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
    });

    return sortedRows;
  }, [guestNameQuery, sortDirection, sortField, stats?.guestNameStats]);

  /* ------------------------------ rendering ------------------------------ */

  if (isLoading && !stats) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Skeleton />
          <Skeleton />
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-lg text-gray-600">Failed to load statistics.</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">
        Statistics and Analytics
      </h1>

      {/* Time range */}
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0">
        <div className="w-full sm:w-auto">
          <select
            className="w-full sm:w-auto border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
          >
            <option value="7days">Last 7 days</option>
            <option value="30days">Last 30 days</option>
            <option value="90days">Last 90 days</option>
            <option value="year">Last year</option>
            <option value="all">All time</option>
          </select>
        </div>
        <div className="text-sm text-gray-500">
          Data updated: {new Date().toLocaleDateString()}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <ul className="flex w-full justify-evenly -mb-px">
          {[
            { key: "overview", icon: ArrowTrendingUpIcon, label: "Overview" },
            { key: "events", icon: ChartBarIcon, label: "Events" },
            { key: "guests", icon: UsersIcon, label: "Guests" },
            { key: "invitations", icon: EnvelopeIcon, label: "Invitations" },
          ].map(({ key, icon: Icon, label }) => (
            <li key={key} className="flex-1 text-center">
              <button
                className={`inline-block py-2 px-1 sm:px-4 font-medium text-center whitespace-nowrap rounded-t-lg border-b-2 w-full ${
                  activeTab === key
                    ? "text-indigo-600 border-indigo-600"
                    : "text-gray-500 border-transparent hover:text-gray-600 hover:border-gray-300"
                }`}
                onClick={() => setActiveTab(key)}
              >
                <span className="flex items-center justify-center sm:justify-start">
                  <Icon className="h-5 w-5 sm:mr-2" />
                  <span className="text-xs sm:text-sm block sm:inline mt-1 sm:mt-0">
                    {label}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* ------------------------------ OVERVIEW ------------------------------ */}
      {activeTab === "overview" && (
        <div>
          {/* KPI desktop */}
          <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-indigo-50 rounded-lg shadow p-4 sm:p-6">
              <h3 className="text-lg font-medium text-gray-500">Total Events</h3>
              <p className="text-2xl sm:text-3xl font-bold text-gray-800 flex items-center">
                {fmt(stats.eventStats.total)}
                <DeltaBadge value={stats.eventStats.growth} />
              </p>
            </div>
            <div className="bg-indigo-50 rounded-lg shadow p-4 sm:p-6">
              <h3 className="text-lg font-medium text-gray-500">Attendance</h3>
              <p className="text-2xl sm:text-3xl font-bold text-gray-800">
                {pct(stats.eventStats.totalAttendance)}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Average registration percentage
              </p>
            </div>
            <div className="bg-indigo-50 rounded-lg shadow p-4 sm:p-6">
              <h3 className="text-lg font-medium text-gray-500">Total Guests</h3>
              <p className="text-2xl sm:text-3xl font-bold text-gray-800">
                {fmt(stats.guestStats.totalAttended)}
              </p>
              <p className="text-sm text-gray-500 mt-2">Registered at events</p>
            </div>
            <div className="bg-indigo-50 rounded-lg shadow p-4 sm:p-6">
              <h3 className="text-lg font-medium text-gray-500">
                Invitation Conversion
              </h3>
              <p className="text-2xl sm:text-3xl font-bold text-gray-800">
                {pct(stats.invitationStats.conversionRate)}
              </p>
              {derived.avgUtil != null && (
                <p className="text-sm text-gray-500 mt-2">
                  Avg. capacity utilization: {pct(derived.avgUtil)}
                </p>
              )}
            </div>
          </div>

          {/* KPI mobile (в ряд) */}
          <div className="flex justify-evenly mb-4 gap-0 sm:hidden">
            {[
              ["Events", fmt(stats.eventStats.total)],
              ["Attend.", pct(stats.eventStats.totalAttendance)],
              ["Guests", fmt(stats.guestStats.totalAttended)],
              ["Conv.", pct(stats.invitationStats.conversionRate)],
            ].map(([label, val]) => (
              <div
                key={label}
                className="py-2 rounded-lg bg-indigo-50 w-[23%] flex flex-col justify-center px-1"
              >
                <p className="text-[10px] text-gray-600 m-0 text-center">
                  {label}
                </p>
                <p className="text-lg font-bold text-gray-800 text-center m-0">
                  {val}
                </p>
              </div>
            ))}
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <h3 className="text-lg font-medium text-gray-800 mb-4">
                Events and Attendance by Month
              </h3>
              {isLoading ? (
                <Skeleton />
              ) : (
                <div className="h-64 sm:h-80 md:h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={stats.eventsByMonth}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      layout="vertical"
                      barSize={20}
                      barGap={5}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis
                        dataKey="month"
                        type="category"
                        width={80}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip />
                      <Legend verticalAlign="top" height={36} />
                      <Bar
                        dataKey="count"
                        name="Number of Events"
                        fill={PALETTE.series1}
                      />
                      <Bar
                        dataKey="attendance"
                        name="Attendance (%)"
                        fill={PALETTE.series2}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <h3 className="text-lg font-medium text-gray-800 mb-4">
                Event Status
              </h3>
              {isLoading ? (
                <Skeleton />
              ) : (
                <div className="h-64 sm:h-80 md:h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={stats.eventsByStatus}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        dataKey="value"
                        nameKey="name"
                        label={renderCustomPieLabel}
                        labelLine={false}
                      >
                        {stats.eventsByStatus.map((entry, i) => {
                          const color =
                            entry.name === "confirmed"
                              ? PALETTE.confirmed
                              : entry.name === "planned"
                              ? PALETTE.planned
                              : entry.name === "cancelled"
                              ? PALETTE.cancelled
                              : COLORS[i % COLORS.length];
                          return <Cell key={i} fill={color} />;
                        })}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>

          {/* Additional metrics */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <h3 className="text-lg font-medium text-gray-800 mb-4">
                Top Venues
              </h3>
              {!stats.topVenues?.length ? (
                <div className="text-sm text-gray-500 p-6 text-center">
                  No data yet
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="sticky top-0 bg-white">
                      <tr>
                        <th
                          className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => handleSort("name")}
                        >
                          Venue <SortIcon field="name" />
                        </th>
                        <th
                          className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => handleSort("count")}
                        >
                          Events <SortIcon field="count" />
                        </th>
                        <th
                          className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => handleSort("attendance")}
                        >
                          Attendance <SortIcon field="attendance" />
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {stats.topVenues.map((venue, index) => (
                        <tr key={index}>
                          <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {venue.name}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                            {fmt(venue.count)}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                            {pct(venue.attendance)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <h3 className="text-lg font-medium text-gray-800 mb-4">
                Guest Distribution by Type
              </h3>
              {isLoading ? (
                <Skeleton />
              ) : (
                <div className="h-64 sm:h-80 md:h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={stats.guestsByAdmissionType}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        dataKey="value"
                        nameKey="name"
                        label={renderCustomPieLabel}
                        labelLine={false}
                      >
                        {stats.guestsByAdmissionType?.map((e, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>

          {/* Guest sources */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-800">
                  Guest Sources
                </h3>
                {stats.guestsBySource?.length > 0 && (
                  <button
                    onClick={() =>
                      downloadCSV(stats.guestsBySource, "guests_by_source.csv")
                    }
                    className="text-xs px-2 py-1 border rounded hover:bg-gray-50"
                  >
                    Export CSV
                  </button>
                )}
              </div>

              {!stats.guestsBySource?.length ? (
                <div className="text-sm text-gray-500 p-6 text-center">
                  No data yet
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="sticky top-0 bg-white">
                      <tr>
                        <th
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => handleSort("name")}
                        >
                          SOURCE <SortIcon field="name" />
                        </th>
                        <th
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => handleSort("value")}
                        >
                          ADDED <SortIcon field="value" />
                        </th>
                        <th
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => handleSort("checkedIn")}
                        >
                          CHECKED IN <SortIcon field="checkedIn" />
                        </th>
                        <th
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => handleSort("rate")}
                        >
                          RATE <SortIcon field="rate" />
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {[...stats.guestsBySource]
                        .sort((a, b) => {
                          if (sortField === "rate") {
                            const rA =
                              a.value > 0
                                ? Math.round(((a.checkedIn || 0) / a.value) * 100)
                                : 0;
                            const rB =
                              b.value > 0
                                ? Math.round(((b.checkedIn || 0) / b.value) * 100)
                                : 0;
                            return sortDirection === "asc" ? rA - rB : rB - rA;
                          }
                          if (!sortField) return 0;
                          if (sortField === "name") {
                            return sortDirection === "asc"
                              ? (a.name || "").localeCompare(b.name || "")
                              : (b.name || "").localeCompare(a.name || "");
                          }
                          const aV = a[sortField] || 0;
                          const bV = b[sortField] || 0;
                          return sortDirection === "asc" ? aV - bV : bV - aV;
                        })
                        .map((s, i) => {
                          const checked = s.checkedIn || 0;
                          const rate =
                            s.value > 0
                              ? Math.round((checked / s.value) * 100)
                              : 0;
                          return (
                            <tr
                              key={i}
                              className={i % 2 === 0 ? "bg-gray-50" : "bg-white"}
                            >
                              <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                                {s.name}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                {fmt(s.value)}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                {fmt(checked)}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                {pct(rate)}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <h3 className="text-lg font-medium text-gray-800 mb-4">
                Guest Check-In by Source
              </h3>
              {isLoading ? (
                <Skeleton />
              ) : (
                <div className="h-64 sm:h-80 md:h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={stats.guestsBySource || []}
                      margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
                      layout="vertical"
                      barSize={20}
                      barGap={5}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" domain={[0, "dataMax"]} />
                      <YAxis
                        dataKey="name"
                        type="category"
                        width={100}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip />
                      <Legend verticalAlign="top" height={36} />
                      {/* стек: Added vs Checked In для легкого сравнения */}
                      <Bar
                        dataKey="value"
                        name="Added"
                        stackId="a"
                        fill={PALETTE.invited}
                      />
                      <Bar
                        dataKey="checkedIn"
                        name="Checked In"
                        stackId="a"
                        fill={PALETTE.attended}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------- EVENTS ------------------------------ */}
      {activeTab === "events" && (
        <div>
          {/* KPI desktop */}
          <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[
              ["Total Events", stats.eventStats.total],
              ["Upcoming", stats.eventStats.upcoming],
              ["Ongoing", stats.eventStats.ongoing],
              ["Past", stats.eventStats.past],
            ].map(([label, val]) => (
              <div key={label} className="bg-indigo-50 rounded-lg shadow p-4 sm:p-6">
                <h3 className="text-lg font-medium text-gray-500">{label}</h3>
                <p className="text-2xl sm:text-3xl font-bold text-gray-800">
                  {fmt(val)}
                </p>
              </div>
            ))}
          </div>

          {/* KPI mobile */}
          <div className="flex justify-evenly mb-4 gap-0 sm:hidden">
            {[
              ["Total", stats.eventStats.total],
              ["Upcoming", stats.eventStats.upcoming],
              ["Ongoing", stats.eventStats.ongoing],
              ["Past", stats.eventStats.past],
            ].map(([label, val]) => (
              <div
                key={label}
                className="py-2 rounded-lg bg-indigo-50 w-[23%] flex flex-col justify-center px-1"
              >
                <p className="text-[10px] text-gray-600 m-0 text-center">
                  {label}
                </p>
                <p className="text-lg font-bold text-gray-800 text-center m-0">
                  {fmt(val)}
                </p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <h3 className="text-lg font-medium text-gray-800 mb-4">
                Average Attendance by Month
              </h3>
              <div className="h-64 sm:h-80 md:h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={stats.eventsByMonth}
                    margin={{ top: 5, right: 30, left: 20, bottom: 25 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" angle={-45} textAnchor="end" height={60} />
                    <YAxis />
                    <Tooltip />
                    <Legend verticalAlign="top" height={36} />
                    <Bar
                      dataKey="attendance"
                      name="Attendance (%)"
                      fill={PALETTE.series1}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <h3 className="text-lg font-medium text-gray-800 mb-4">
                Number of Events by Month
              </h3>
              <div className="h-64 sm:h-80 md:h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={stats.eventsByMonth}
                    margin={{ top: 5, right: 30, left: 20, bottom: 25 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" angle={-45} textAnchor="end" height={60} />
                    <YAxis />
                    <Tooltip />
                    <Legend verticalAlign="top" height={36} />
                    <Area
                      type="monotone"
                      dataKey="count"
                      name="Number of Events"
                      fill={PALETTE.series1}
                      stroke={PALETTE.series1}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <h3 className="text-lg font-medium text-gray-800 mb-4">
                Event Status
              </h3>
              <div className="h-64 sm:h-80 md:h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={stats.eventsByStatus}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="value"
                      nameKey="name"
                      label={renderCustomPieLabel}
                      labelLine={false}
                    >
                      {stats.eventsByStatus?.map((entry, i) => {
                        const color =
                          entry.name === "confirmed"
                            ? PALETTE.confirmed
                            : entry.name === "planned"
                            ? PALETTE.planned
                            : entry.name === "cancelled"
                            ? PALETTE.cancelled
                            : COLORS[i % COLORS.length];
                        return <Cell key={i} fill={color} />;
                      })}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <h3 className="text-lg font-medium text-gray-800 mb-4">
                Top Venues by Attendance
              </h3>
              <div className="h-64 sm:h-80 md:h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={stats.topVenues}
                    margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={120} />
                    <Tooltip />
                    <Legend verticalAlign="top" height={36} />
                    <Bar
                      dataKey="attendance"
                      name="Attendance (%)"
                      fill={PALETTE.series1}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------ GUESTS ------------------------------- */}
      {activeTab === "guests" && (
        <div>
          {/* KPI desktop */}
          <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[
              ["Total Invited", fmt(stats.guestStats.totalInvited)],
              ["Total Attended", fmt(stats.guestStats.totalAttended)],
              ["No-show Rate", pct(stats.guestStats.noShowRate)],
              ["Average +1", fmt(stats.guestStats.avgPlusOnes)],
            ].map(([label, val]) => (
              <div key={label} className="bg-indigo-50 rounded-lg shadow p-4 sm:p-6">
                <h3 className="text-lg font-medium text-gray-500">{label}</h3>
                <p className="text-2xl sm:text-3xl font-bold text-gray-800">
                  {val}
                </p>
              </div>
            ))}
          </div>

          {/* KPI mobile */}
          <div className="flex justify-evenly mb-4 gap-0 sm:hidden">
            {[
              ["Invited", fmt(stats.guestStats.totalInvited)],
              ["Attended", fmt(stats.guestStats.totalAttended)],
              ["No-show", pct(stats.guestStats.noShowRate)],
              ["Avg. +1", fmt(stats.guestStats.avgPlusOnes)],
            ].map(([label, val]) => (
              <div
                key={label}
                className="py-2 rounded-lg bg-indigo-50 w-[23%] flex flex-col justify-center px-1"
              >
                <p className="text-[10px] text-gray-600 m-0 text-center">
                  {label}
                </p>
                <p className="text-lg font-bold text-gray-800 text-center m-0">
                  {val}
                </p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <h3 className="text-lg font-medium text-gray-800 mb-4">
                Distribution by Type
              </h3>
              <div className="h-64 sm:h-80 md:h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={stats.guestsByAdmissionType}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="value"
                      nameKey="name"
                      label={renderCustomPieLabel}
                      labelLine={false}
                    >
                      {stats.guestsByAdmissionType?.map((e, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <h3 className="text-lg font-medium text-gray-800 mb-4">
                Recent Events Attendance
              </h3>
              <div className="h-64 sm:h-80 md:h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={stats.recentEventsAttendance || []}
                    margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis
                      dataKey="name"
                      type="category"
                      width={120}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip />
                    <Legend verticalAlign="top" height={36} />
                    <Bar dataKey="invited" name="Invited" fill={PALETTE.invited} />
                    <Bar dataKey="attended" name="Attended" fill={PALETTE.attended} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Check-in rate by type (с учётом detailed, если есть) */}
          <div className="grid grid-cols-1 mb-8">
            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <h3 className="text-lg font-medium text-gray-800 mb-4">
                Check-in Rate by Guest Type
              </h3>
              <div className="h-64 sm:h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[
                      {
                        name: "Overall",
                        rate: derived.overallRate,
                        invited: stats.guestStats.totalInvited,
                        attended: stats.guestStats.totalAttended,
                      },
                      ...(stats.guestsByAdmissionTypeDetailed
                        ? stats.guestsByAdmissionTypeDetailed
                            .filter((t) => t.invited > 0)
                            .map((t) => ({
                              name: t.name,
                              rate: t.checkInRate,
                              invited: t.invited,
                              attended: t.attended,
                            }))
                        : (stats.guestsByAdmissionType || []).map((t) => ({
                            name: t.name,
                            rate: 0,
                            invited: t.value || 0,
                            attended: 0,
                          }))),
                    ]}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={80} />
                    <Tooltip
                      formatter={(value, name) => {
                        if (name === "rate") return [`${value}%`, "Check-in Rate"];
                        if (name === "invited") return [value, "Total Invited"];
                        if (name === "attended") return [value, "Total Attended"];
                        return [value, name];
                      }}
                    />
                    <Legend />
                    <Bar dataKey="rate" name="Check-in Rate (%)" fill={PALETTE.series1} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 text-sm text-gray-500">
                This shows which guest categories have the highest attendance
                rates and whether discounts actually drive attendance.
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 mt-8">
            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-800">
                  Guest Names
                </h3>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    value={guestNameQuery}
                    onChange={(event) => setGuestNameQuery(event.target.value)}
                    placeholder="Search names..."
                    className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  {guestNameRows.length > 0 && (
                    <button
                      onClick={() =>
                        downloadCSV(guestNameRows, "guest_name_stats.csv")
                      }
                      className="text-xs px-2 py-1 border rounded hover:bg-gray-50"
                    >
                      Export CSV
                    </button>
                  )}
                </div>
              </div>
              {!guestNameRows.length ? (
                <div className="text-sm text-gray-500 p-6 text-center">
                  No data yet
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="sticky top-0 bg-white">
                      <tr>
                        <th
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => handleSort("guestName")}
                        >
                          Name <SortIcon field="guestName" />
                        </th>
                        <th
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => handleSort("addedCount")}
                        >
                          Added <SortIcon field="addedCount" />
                        </th>
                        <th
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => handleSort("attendedCount")}
                        >
                          Attended <SortIcon field="attendedCount" />
                        </th>
                        <th
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => handleSort("attendanceRate")}
                        >
                          Rate <SortIcon field="attendanceRate" />
                        </th>
                        <th
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => handleSort("eventCount")}
                        >
                          Events <SortIcon field="eventCount" />
                        </th>
                        <th
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => handleSort("lastEventAt")}
                        >
                          Last Event <SortIcon field="lastEventAt" />
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {guestNameRows.map((guest, index) => (
                        <tr
                          key={`${guest.name}-${index}`}
                          className={index % 2 === 0 ? "bg-gray-50" : "bg-white"}
                        >
                          <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                            {guest.name}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                            {fmt(guest.addedCount)}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                            {fmt(guest.attendedCount)}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                            {pct(guest.attendanceRate)}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                            {fmt(guest.eventCount)}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                            {fmtDate(guest.lastEventAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Guest sources (повтор для вкладки — компактная таблица) */}
          <div className="grid grid-cols-1 mt-8">
            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-800">Guest Sources</h3>
                {stats.guestsBySource?.length > 0 && (
                  <button
                    onClick={() =>
                      downloadCSV(stats.guestsBySource, "guests_by_source.csv")
                    }
                    className="text-xs px-2 py-1 border rounded hover:bg-gray-50"
                  >
                    Export CSV
                  </button>
                )}
              </div>
              {!stats.guestsBySource?.length ? (
                <div className="text-sm text-gray-500 p-6 text-center">
                  No data yet
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="sticky top-0 bg-white">
                      <tr>
                        <th
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => handleSort("name")}
                        >
                          SOURCE <SortIcon field="name" />
                        </th>
                        <th
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => handleSort("value")}
                        >
                          ADDED <SortIcon field="value" />
                        </th>
                        <th
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => handleSort("checkedIn")}
                        >
                          CHECKED IN <SortIcon field="checkedIn" />
                        </th>
                        <th
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => handleSort("rate")}
                        >
                          RATE <SortIcon field="rate" />
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {[...stats.guestsBySource]
                        .sort((a, b) => {
                          if (sortField === "rate") {
                            const rA =
                              a.value > 0
                                ? Math.round(((a.checkedIn || 0) / a.value) * 100)
                                : 0;
                            const rB =
                              b.value > 0
                                ? Math.round(((b.checkedIn || 0) / b.value) * 100)
                                : 0;
                            return sortDirection === "asc" ? rA - rB : rB - rA;
                          }
                          if (!sortField) return 0;
                          if (sortField === "name") {
                            return sortDirection === "asc"
                              ? (a.name || "").localeCompare(b.name || "")
                              : (b.name || "").localeCompare(a.name || "");
                          }
                          const aV = a[sortField] || 0;
                          const bV = b[sortField] || 0;
                          return sortDirection === "asc" ? aV - bV : bV - aV;
                        })
                        .map((s, i) => {
                          const checked = s.checkedIn || 0;
                          const rate =
                            s.value > 0
                              ? Math.round((checked / s.value) * 100)
                              : 0;
                          return (
                            <tr key={i} className={i % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                              <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                                {s.name}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                {fmt(s.value)}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                {fmt(checked)}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                {pct(rate)}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ---------------------------- INVITATIONS ---------------------------- */}
      {activeTab === "invitations" && (
        <div>
          {/* KPI desktop */}
          <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[
              ["Sent", fmt(stats.invitationStats.sent)],
              ["Opened", fmt(stats.invitationStats.opened)],
              ["Accepted", fmt(stats.invitationStats.accepted)],
              ["Conversion", pct(stats.invitationStats.conversionRate)],
            ].map(([label, val]) => (
              <div key={label} className="bg-indigo-50 rounded-lg shadow p-4 sm:p-6">
                <h3 className="text-lg font-medium text-gray-500">{label}</h3>
                <p className="text-2xl sm:text-3xl font-bold text-gray-800">
                  {val}
                </p>
                {label === "Opened" && stats.invitationStats.sent > 0 && (
                  <p className="text-sm text-gray-500 mt-2">
                    {pct(
                      (stats.invitationStats.opened / stats.invitationStats.sent) *
                        100
                    )}{" "}
                    of sent
                  </p>
                )}
                {label === "Accepted" && stats.invitationStats.sent > 0 && (
                  <p className="text-sm text-gray-500 mt-2">
                    {pct(
                      (stats.invitationStats.accepted /
                        stats.invitationStats.sent) *
                        100
                    )}{" "}
                    of sent
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* KPI mobile */}
          <div className="flex justify-evenly mb-4 gap-0 sm:hidden">
            {[
              ["Sent", fmt(stats.invitationStats.sent)],
              ["Opened", fmt(stats.invitationStats.opened)],
              ["Accepted", fmt(stats.invitationStats.accepted)],
              ["Conv.", pct(stats.invitationStats.conversionRate)],
            ].map(([label, val]) => (
              <div
                key={label}
                className="py-2 rounded-lg bg-indigo-50 w-[23%] flex flex-col justify-center px-1"
              >
                <p className="text-[10px] text-gray-600 m-0 text-center">
                  {label}
                </p>
                <p className="text-lg font-bold text-gray-800 text-center m-0">
                  {val}
                </p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <h3 className="text-lg font-medium text-gray-800 mb-4">
                Invitation Funnel
              </h3>
              <div className="h-64 sm:h-80 md:h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[
                      { name: "Sent", value: stats.invitationStats.sent },
                      { name: "Opened", value: stats.invitationStats.opened },
                      { name: "Accepted", value: stats.invitationStats.accepted },
                      {
                        name: "Attended",
                        value: stats.invitationStats.attended || 0,
                      },
                    ]}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend verticalAlign="top" height={36} />
                    <Bar dataKey="value" name="Quantity" fill={PALETTE.series1} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <h3 className="text-lg font-medium text-gray-800 mb-4">
                Conversion Rate Over Time
              </h3>
              <div className="h-64 sm:h-80 md:h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={stats.invitationConversionTrend || []}
                    margin={{ top: 5, right: 30, left: 20, bottom: 25 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" angle={-45} textAnchor="end" height={60} />
                    <YAxis />
                    <Tooltip />
                    <Legend verticalAlign="top" height={36} />
                    <Line
                      dataKey="rate"
                      name="Conversion (%)"
                      stroke={PALETTE.series1}
                      activeDot={{ r: 8 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Top inviters */}
          {stats.topInviters?.length > 0 && (
            <div className="grid grid-cols-1 mt-8">
              <div className="bg-white rounded-lg shadow p-4 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-800">
                    Top Inviters
                  </h3>
                  <button
                    onClick={() =>
                      downloadCSV(stats.topInviters, "top_inviters.csv")
                    }
                    className="text-xs px-2 py-1 border rounded hover:bg-gray-50"
                  >
                    Export CSV
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="sticky top-0 bg-white">
                      <tr>
                        <th
                          className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => handleSort("name")}
                        >
                          Name <SortIcon field="name" />
                        </th>
                        <th
                          className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => handleSort("guests_added")}
                        >
                          Guests Added <SortIcon field="guests_added" />
                        </th>
                        <th
                          className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => handleSort("guests_attended")}
                        >
                          Attended <SortIcon field="guests_attended" />
                        </th>
                        <th
                          className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => handleSort("conversion")}
                        >
                          Conversion <SortIcon field="conversion" />
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {stats.topInviters.map((inv, i) => {
                        const conv =
                          inv.guests_added > 0
                            ? Math.round(
                                (inv.guests_attended / inv.guests_added) * 100
                              )
                            : 0;
                        return (
                          <tr key={i}>
                            <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {inv.name}
                            </td>
                            <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                              {fmt(inv.guests_added)}
                            </td>
                            <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                              {fmt(inv.guests_attended)}
                            </td>
                            <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                              {pct(conv)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StatisticsDashboard;
